import { Fragment, type FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { getRoundConfig } from 'app/config/videogameSpecific.config.helper';
import { getRounds, getTotalScores } from 'app/utils/rounds';
import { withBackgroundImage } from 'app/utils/style';
import MapNameTag from 'app/components/MapNameTag';
import { getCDNUrl } from 'app/utils/api_url';

import style from './style.module.scss';

interface Props {
  game: BracketGameCs;
  variation: 'cs-2' | 'cs-go' | 'valorant';
  opponent1: BracketMatchOpponent | undefined;
  opponent2: BracketMatchOpponent | undefined;
}

const BracketGameRecapCs: FC<Props> = ({ game, variation, opponent1, opponent2 }) => {
  const { t } = useTranslation();

  const roundConfig = getRoundConfig(variation);
  const teamIds: [number, number] = [opponent1?.id ?? -1, opponent2?.id ?? -1];

  const winnerByForfeitTeamIndex = game.forfeit
    ? teamIds.findIndex(id => id === game.winner_id)
    : -1;

  const { halfgroups, overtime } = getRounds(game.rounds, teamIds, roundConfig);
  const [team1Score, team2Score] = getTotalScores(game.opponents, teamIds);

  if (game.status === 'finished' && !game.complete) {
    return null;
  }

  if (game.status === 'not_started') {
    return null;
  }

  return (
    <div
      className={style.gameRecap}
      style={game.map ? withBackgroundImage(getCDNUrl(game.map.image_url)) : {}}
    >
      <div className={style.backgroundProtection}>
        <div
          className={classNames(style.opponentScore, {
            [style.loser]: team1Score !== null && team2Score !== null && team1Score < team2Score
          })}
        >
          {game.status === 'running' ? (
            <div className={style.mapInProgress}>
              {t('component_bracket-match-recap_map-in-progress')}
            </div>
          ) : (
            <>
              {winnerByForfeitTeamIndex !== -1 ? (
                <>
                  {winnerByForfeitTeamIndex === 0 ? (
                    <div className={style.mapScore}>Winner</div>
                  ) : (
                    <div className={style.forfeitTag}>{t('blocks_bracket_match_forfeit-long')}</div>
                  )}
                </>
              ) : (
                <>
                  <div className={style.mapScore}>{team1Score}</div>
                  <div className={style.rounds}>
                    <span>{'('}</span>
                    {halfgroups.map(({ teams: [team1] }, index) => (
                      <Fragment key={`halfgroup-${index}-team1`}>
                        {index !== 0 && ','}
                        <span className={style[team1.side ?? 'scoreDefault']}>{team1.score}</span>
                      </Fragment>
                    ))}
                    {overtime && <span>,{overtime.teamsScore[0]}</span>}
                    <span>{')'}</span>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <MapNameTag name={game.map?.name} />
        <div
          className={classNames(style.opponentScore, style.reversed, {
            [style.loser]: team1Score !== null && team2Score !== null && team1Score > team2Score
          })}
        >
          {game.status !== 'running' && (
            <>
              {winnerByForfeitTeamIndex !== -1 ? (
                <>
                  {winnerByForfeitTeamIndex === 1 ? (
                    <div className={style.mapScore}>Winner</div>
                  ) : (
                    <div className={style.forfeitTag}>{t('blocks_bracket_match_forfeit-long')}</div>
                  )}
                </>
              ) : (
                <>
                  <div className={style.mapScore}>{team2Score}</div>
                  <div className={style.rounds}>
                    <span>{'('}</span>
                    {halfgroups.map(({ teams: [_, team2] }, index) => (
                      <Fragment key={`halfgroup-${index}-team2`}>
                        {index !== 0 && ','}
                        <span className={style[team2.side ?? 'scoreDefault']}>{team2.score}</span>
                      </Fragment>
                    ))}
                    {overtime && <span>,{overtime.teamsScore[1]}</span>}
                    <span>{')'}</span>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

BracketGameRecapCs.displayName = 'BracketGameRecapCs';

export default BracketGameRecapCs;
